import type { ChatModelAdapter, ThreadAssistantMessagePart } from "@assistant-ui/react";
import type { components } from "@acto/taskpane/core/data-access/meeting_prep/generated-types";
import type { ChatStreamResponse, TextEvent, TableEvent } from "./streamTypes";
import { streamEvents } from "./sseUtils";
import { isTextEvent, isTableEvent, isEndEvent } from "./typeGuards";
import type { CustomerContext } from "./types";
import type { AmbientContextEnvelope } from "@acto/ambient-context-schema";

function getApiBaseUrl(): string {
  const url = import.meta.env.VITE_INTELLIGENCE_LAYER_API_URL;
  if (!url) {
    throw new Error("VITE_INTELLIGENCE_LAYER_API_URL environment variable is required");
  }
  return url;
}

// The Agents' generated ChatRequest does not (yet) declare `envelope`; we send
// it additively as a prototype and they read it tolerantly. Extend locally.
type ChatRequest = components["schemas"]["ChatRequest"] & {
  envelope?: AmbientContextEnvelope | null;
};

export function createIntelligenceAdapter(
  accessToken: string,
  getSessionId: () => string,
  getCustomerContext: () => CustomerContext | null,
  locale: string,
  getEnvelope: () => AmbientContextEnvelope | null = () => null,
): ChatModelAdapter {
  return {
    run: async function* ({ messages, abortSignal }) {
      const sessionId = getSessionId();
      const customerContext = getCustomerContext();
      const envelope = getEnvelope();

      try {
        const lastUserMessage = [...messages].reverse().find((msg) => msg.role === "user");
        if (!lastUserMessage) {
          yield {
            content: [{ type: "text" as const, text: "No user message found" }],
            status: { type: "incomplete" as const, reason: "error" as const },
          };
          return;
        }

        const messageText = lastUserMessage.content.find((part) => part.type === "text")?.text ?? "";

        const contextEntities: components["schemas"]["EntityContext"][] = [];
        if (customerContext) {
          contextEntities.push({
            entity_type: "client",
            entity_ids: [customerContext.externalReference],
          });
        }

        const requestBody: ChatRequest = {
          prompt: messageText,
          session_id: sessionId,
          context_entities: contextEntities,
          ...(envelope ? { envelope } : {}),
        };

        const response = await fetch(`${getApiBaseUrl()}/chat/message`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "Accept-Language": locale,
            "X-Acto-Locale": locale,
          },
          body: JSON.stringify(requestBody),
          signal: abortSignal,
        });

        if (!response.ok) {
          yield {
            content: [
              {
                type: "text" as const,
                text: `Error: ${response.status} ${response.statusText}`,
              },
            ],
            status: { type: "incomplete" as const, reason: "error" as const },
          };
          return;
        }

        let accumulatedText = "";
        const toolCalls: ThreadAssistantMessagePart[] = [];

        for await (const event of streamEvents(response)) {
          try {
            const parsed: ChatStreamResponse = JSON.parse(event.data);
            const message = parsed.message;

            if (!message) {
              continue;
            }

            if (isTextEvent(message)) {
              accumulatedText += (message as TextEvent).text;

              yield {
                content: [{ type: "text" as const, text: accumulatedText }, ...toolCalls],
              };
            } else if (isTableEvent(message)) {
              const tableMsg = message as TableEvent;
              toolCalls.push({
                type: "tool-call" as const,
                toolCallId: crypto.randomUUID(),
                toolName: "display_table",
                args: {
                  title: tableMsg.title,
                  ...(tableMsg.description && { description: tableMsg.description }),
                },
                result: { data: tableMsg.data },
                argsText: tableMsg.title,
              });

              yield {
                content: [...(accumulatedText ? [{ type: "text" as const, text: accumulatedText }] : []), ...toolCalls],
              };
            } else if (isEndEvent(message)) {
              break;
            }
          } catch (parseError) {
            accumulatedText += `\n\nError parsing stream event: ${parseError instanceof Error ? parseError.message : "Unknown error"}`;

            yield {
              content: [{ type: "text" as const, text: accumulatedText }],
            };
          }
        }

        yield {
          content: [...(accumulatedText ? [{ type: "text" as const, text: accumulatedText }] : []), ...toolCalls],
          status: { type: "complete" as const, reason: "stop" as const },
        };
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          yield {
            content: [{ type: "text" as const, text: "Request cancelled" }],
            status: { type: "incomplete" as const, reason: "cancelled" as const },
          };
          return;
        }

        yield {
          content: [
            {
              type: "text" as const,
              text: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
            },
          ],
          status: { type: "incomplete" as const, reason: "error" as const },
        };
      }
    },
  };
}
